import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { Check } from "lucide-react";
import { PriceTag } from "@/components/PriceTag";
import ActivateButton from "@/components/ActivateButton";

export default function ProductDetail() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/services/${slug}`)
      .then((r) => {
        setSvc(r.data);
        setSelectedPlan(r.data.plans[0]?.code);
      })
      .catch(() => setErr("Service not found"));
  }, [slug]);

  if (err) {
    return (
      <div className="container-pad py-24 font-mono text-zinc-400" data-testid="service-error">
        // {err}
      </div>
    );
  }
  if (!svc) {
    return <div className="container-pad py-24 font-mono text-zinc-500">LOADING…</div>;
  }

  const plan = svc.plans.find((p) => p.code === selectedPlan);

  return (
    <div data-testid="product-detail-page" className="container-pad py-12 md:py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        {/* LEFT — info */}
        <div className="md:col-span-7">
          <div className="flex items-center gap-2">
            <span className="badge-mono">
              {svc.subtype === "dedicated" ? "DEDICATED" : "VPS"}
            </span>
            <span className="badge-mono">{svc.subtype.replace("_", " ")}</span>
            <span className="badge-mono">
              STOCK: {svc.stock === -1 ? "∞" : svc.stock}
            </span>
          </div>
          <h1 className="mt-5 font-display text-4xl md:text-5xl tracking-tight">{svc.name}</h1>
          <p className="mt-3 text-zinc-400 text-lg">{svc.tagline}</p>
          <p className="mt-6 text-zinc-300 leading-relaxed">{svc.description}</p>

          <div className="mt-10">
            <div className="overline mb-3">// FEATURES</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6">
              {svc.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                  <Check size={16} className="mt-0.5 text-signal shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* RIGHT — plan select */}
        <div className="md:col-span-5">
          <div className="card-flat sticky top-24">
            <div className="border-b border-zinc-800 p-5">
              <div className="overline">// SELECT PLAN</div>
            </div>
            <div className="p-2">
              {svc.plans.map((p) => {
                const active = p.code === selectedPlan;
                return (
                  <button
                    key={p.code}
                    data-testid={`plan-${p.code}`}
                    onClick={() => setSelectedPlan(p.code)}
                    className={`w-full text-left p-4 rounded-sm border transition-colors mb-1 last:mb-0 ${
                      active
                        ? "border-signal bg-zinc-900"
                        : "border-transparent hover:border-zinc-800"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-display text-lg">{p.name}</div>
                        {p.quota && (
                          <div className="font-mono text-[11px] text-zinc-500 mt-0.5">{p.quota}</div>
                        )}
                      </div>
                      <PriceTag amount={p.price_usd} period={p.period === "month" ? "mo" : p.period} />
                    </div>
                    {p.highlights && p.highlights.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {p.highlights.map((h) => (
                          <span key={h} className="badge-mono">{h}</span>
                        ))}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="border-t border-zinc-800 p-5">
              <ActivateButton
                serviceSlug={svc.slug}
                planCode={plan?.code}
                dailyCost={plan ? plan.price_usd / 30 : 0}
                monthlyCost={plan?.price_usd}
                fullWidth
                className="btn-signal h-12 text-sm font-mono tracking-widest"
                testId="checkout-continue-button"
              >
                {plan ? `ACTIVATE · $${(plan.price_usd / 30).toFixed(2)}/day` : "ACTIVATE"}
              </ActivateButton>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-zinc-600 text-center">
                Daily fee deducted from your CloudRent balance · ${plan?.price_usd?.toFixed(2)}/mo
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
